import React from 'react'
import qs from 'query-string'
import _ from 'lodash'
import DOMPurify from 'dompurify'
import {
  entryPointId,
  __prod__,
  LATITUDE_PATTERN,
  LONGITUDE_PATTERN,
  apiEndpoints,
  ADDITIONAL_SEARCHBAR_SELECTS,
  __local_cdn__,
  __dev__,
} from 'settings/app/constants'
import labels from './staticLabels.json'

/**
 * get highlighted string for rendering
 * @returns {[]}
 * @param highlightedString
 * @param matchNumber (for cypress test)
 */
export const parseHighlightedString = (highlightedString, matchNumber) =>
  highlightedString
    .split(/<em>(.*?)<\/em>/gi)
    .reduce((parsedArr, string, i) => {
      // skip empty string
      if (string === '') return parsedArr
      if (i % 2 === 0) {
        parsedArr.push(
          <span
            key={ `strong-${ matchNumber }-${ i }` }
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(string) }}
          />
        )
      } else {
        parsedArr.push(
          <strong
            key={ `strong-${ matchNumber }-${ i }` }
            id={ `strong-${ matchNumber }-${ i }` }
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(string) }}
          />
        )
      }

      return parsedArr
    }, [])

/**
 * Get Object with selected filter options
 * @param {Array} collection
 * @return {Object}
 */
export const getFilterObject = (collection = []) =>
  collection.reduce((result, filter) => {
    if (
      Object.prototype.hasOwnProperty.call(filter, 'type') &&
      filter.type === 'modal'
    ) {
      const activeFilters = {}
      filter.tabs.forEach((tab) => {
        tab.options.forEach((option) => {
          option.options.forEach((subOption) => {
            if (subOption.selected) {
              activeFilters[subOption.key] = subOption.selected
            }
          })
        })
      })
      return {
        ...result,
        ...activeFilters,
      }
    }

    // Group filters selected options as array
    if (filter.hasOwnProperty('options')) {
      if ('custom_opening_hours' !== filter.key) {
        return {
          ...result,
          [filter.key]: filter.options
            .filter((option) => option.selected)
            .map((option) => option.key),
        }
      }
      return {
        ...result,
        opening_day: filter.options[0].selected,
        opening_time: filter.options[1].selected,
      }
    }

    return { ...result, [filter.key]: filter.selected }
  }, {})

/**
 * replace string
 * @param search
 * @param replaceWith
 * @returns {*}
 */
export const getReplacedString = ({ search, replaceWith }) =>
  search.replace('__SEARCH__TERM__', replaceWith)

/**
 * set/reset opening times filter
 * notice (like updateOpeningTimesFilter) but not for boolean based time filter
 * @param collection
 * @param filterObject
 */
// TODO optimized this function, its not the best way to update time filter
export const updateOpeningTimesFilter = (collection, filterObject) =>
  Object.keys(collection).map((index) => {
    // if booth values are equal then reset || update case
    if ('custom_opening_hours' === collection[index].key) {
      if (
        collection[index].options[0].selected === filterObject.opening_day &&
        collection[index].options[1].selected === filterObject.opening_time
      ) {
        collection[index].options[0].selected = ''
        collection[index].options[1].selected = ''
      } else {
        collection[index].options[0].selected = filterObject.opening_day
        collection[index].options[1].selected = filterObject.opening_time
      }
    }
    return collection[index]
  })

/**
 * set cookie with expire date in days
 * @param cname
 * @param cvalue
 * @param exdays
 * @returns void
 */
export const setCookie = (cname, cvalue, exdays = 0) => {
  let cookieString = `${ cname }=${ cvalue }`

  // add expire if not 0
  if (exdays !== 0) {
    const date = new Date()
    date.setTime(date.getTime() + exdays * 24 * 60 * 60 * 1000)
    const expires = `expires=${ date.toUTCString() }`
    cookieString += `;${ expires }`
  }

  // add path
  cookieString += ';path=/'

  document.cookie = cookieString
}

/**
 * read cookie by name and return just the value (no expire date and other fields)
 * @param name the cookie name to get the value
 * @param decode default true to decodeURIComponent
 * @param parseAsJSON default false, if set to true the value will be parsed as JSON
 * @returns {null|json|string}
 */
export const getCookieValueByName = (
  name,
  decode = true,
  parseAsJSON = false
) => {
  // need try-catch to handle "SecurityError: The operation is insecure." if cookies are disabled
  try {
    const value = `; ${ document.cookie }`
    const parts = value.split(`; ${ name }=`)
    if (parts.length >= 2) {
      const cookieValue = parts.pop().split(';').shift()
      const decodedValue =
        decode && cookieValue ? decodeURIComponent(cookieValue) : cookieValue

      return parseAsJSON && decodedValue
        ? JSON.parse(decodeURIComponent(decodedValue))
        : decodedValue
    }
    return null
  } catch (e) {
    console.log(e)
  }

  return null
}

/**
 * return an object with the 'clientId' and 'clientName'
 * @param cookieName
 */
export const getCookieClient = (cookieName = 'localisation-v2') => {
  const cookieValue = getCookieValueByName(cookieName, true, true)

  let clientId = null
  let clientName = null

  // get client id and value if exists in cookieValue
  if (cookieValue !== null && cookieValue.hasOwnProperty('client')) {
    const clientValue = Object.keys(cookieValue.client)

    if (clientValue.length > 0) {
      clientId = cookieValue.client.id
      clientName = cookieValue.client.title
    }
  }

  return {
    clientId,
    clientName: clientName ? clientName.replace(/\+/g, ' ') : null,
  }
}

/**
 * search recursive for key duplications
 * @param array
 * @param searchKey
 * @param exclude
 * @param parentKey
 * @returns {boolean}
 */
export const searchRecursiveForDuplicate = (
  array,
  searchKey,
  exclude,
  parentKey = false
) => {
  let found = false

  // eslint-disable-next-line array-callback-return
  array.some((filter, index) => {
    if (filter.type === 'group') {
      found = searchRecursiveForDuplicate(
        filter.options,
        searchKey,
        exclude,
        filter.key
      )
      if (found) {
        return true
      }
    }

    if (
      (exclude.parentKey !== parentKey || exclude.index !== index) &&
      filter.key === searchKey
    ) {
      found = parentKey
      return true
    }
  })

  return found
}

/**
 * get qsr rating by scoring
 * @param rating
 * @returns {number}
 */
export const getQsrValueByScoring = ({ rating }) =>
  'low' === rating ? 1 : 'average' === rating ? 2 : 3

/**
 * get a deep copy from an array
 * @param array
 * @returns {*}
 */
export const getDeepCopy = (array) =>
  array.map((item) => (Array.isArray(item) ? getDeepCopy(item) : item))

/**
 * merge two array with out duplications
 * @returns {*}
 * @param arr1
 * @param arr2
 */
export const mergeArraysWithoutDuplications = (arr1, arr2) =>
  arr1.concat(arr2.filter((item) => arr1.indexOf(item) < 0))

/**
 * generate a uid
 */
export const genUID = () =>
  Math.floor((1 + Math.random()) * 0x1000000)
    .toString(16)
    .substring(1)

/**
 * check for plain object
 * @param item
 * @returns {boolean}
 */
export function isPlainObject(item) {
  return item && typeof item === 'object' && item.constructor === Object
}

/**
 * get a deep merged object
 * @param target
 * @param source
 * @param options
 * @returns {*}
 */
export function deepmerge(target, source, options = { clone: true }) {
  const output = options.clone ? { ...target } : target

  if (isPlainObject(target) && isPlainObject(source)) {
    Object.keys(source).forEach((key) => {
      // Avoid prototype pollution
      if (key === '__proto__') {
        return
      }

      if (isPlainObject(source[key]) && key in target) {
        output[key] = deepmerge(target[key], source[key], options)
      } else {
        output[key] = source[key]
      }
    })
  }

  return output
}

/**
 * Safe chained function
 *
 * Will only create a new function if needed,
 * otherwise will pass back existing functions or null.
 *
 * @returns {function|null}
 * @param funcs
 */
export function createChainedFunction(...funcs) {
  return funcs.reduce(
    (acc, func) => {
      if (func == null) {
        return acc
      }

      if (!__prod__) {
        if (typeof func !== 'function') {
          console.error(
            'Invalid Argument Type, must only provide functions, undefined, or null.'
          )
        }
      }

      return function chainedFunction(...args) {
        acc.apply(this, args)
        func.apply(this, args)
      }
    },
    () => {}
  )
}

/**
 * A strict capitalization should uppercase the first letter of each word a the sentence.
 * We only handle the first word.
 * @param string
 * @returns {string}
 */
export function capitalize(string) {
  if (typeof string !== 'string') {
    throw new Error('capitalize(string) expects a string argument.')
  }

  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * string highlighter (used in cluster and storybook)
 * @param match
 * @returns {string}
 */
export const getHighlightedString = (match) => {
  let highlightedString = ''

  if (match.highlight.hasOwnProperty('name')) {
    highlightedString += `${ match.highlight.name[0] } `
  } else {
    highlightedString += `${ match.source.name } `
  }

  if (match.highlight.hasOwnProperty('code')) {
    highlightedString += `(${ match.highlight.code[0] })`
  } else {
    if (match.source.hasOwnProperty('type')) {
      highlightedString += `(${ match.source.type } `
    }
    if (match.source.hasOwnProperty('code')) {
      highlightedString += `${ match.source.code })`
    }
  }
  return highlightedString
}

/**
 * detecting apple devices
 * @returns {boolean}
 */
export const isIphone = () => !!navigator.userAgent.match(/iPhone/i)

/**
 * get entry point
 * @returns {string}
 */
export const getEntryPoint = () => localStorage.getItem('entryPoint')

/**
 * get page type
 * @returns {string}
 */
export const getPageType = () => localStorage.getItem('pageType') || false

/**
 * get asset path
 * @returns {string}
 */
export const getAssetPath = () => {
  if (__dev__) {
    return ''
  } else if (__prod__ && __local_cdn__) {
    return 'https://navigatoren-cdn.aok.de/' // rewrite to live cdn
  }
  return localStorage.getItem('assetPath') || ''
}

/**
 * get meta element by name
 * @param name
 * @returns {*|boolean}
 */
export const getMetaElementByName = (name) => {
  const metaElements = [...document.getElementsByTagName('meta')]

  if (metaElements.length > 0) {
    const searchFor = metaElements.find((el) => el.name === name)
    return searchFor !== undefined ? searchFor : false
  }

  return false
}

/**
 * get element an scroll to position
 * @param element
 * @param isAccordion
 * @param timeout
 * @returns {boolean}
 */
export const scrollToElement = (element, isAccordion, timeout = 300) => {
  const scrollToTargetElement = document.getElementById(element)

  if (!scrollToTargetElement) return false

  // when scrollToElement has accord and its closed, then open before scroll to
  if (isAccordion) {
    const accordion = document.querySelector(
      `#${ element } > div > div:first-child`
    )

    if (!!accordion && accordion.getAttribute('aria-expanded') === 'false') {
      accordion.click()
    }
  }

  setTimeout(() => {
    window.scrollTo({
      top: scrollToTargetElement.getBoundingClientRect().top,
      behavior: 'smooth',
    })
  }, timeout)
}

/**
 * decode save html to string
 * @param string
 * @returns {string}
 * https://stackoverflow.com/questions/1912501/unescape-html-entities-in-javascript
 */
export const htmlDecode = (string) => {
  const doc = new DOMParser().parseFromString(string, 'text/html')
  return doc.documentElement.textContent
}

/**
 * get formatted price in a currency
 * if price param given is not a number returns given noValueString param
 * if price param given equals 0 and showZeroPrice param is false returns given noValueString param
 * @param {number|string} price
 * @param {boolean} showZeroPrice
 * @param {string} noValueString
 * @param {object} options
 * @param {number} decimalPlaces
 * @param {string} locale
 * @param {string} currency
 * @returns {string}
 */
export const formatPrice = ({
  price,
  showZeroPrice = false,
  noValueString = '-',
  options: { decimalPlaces = 2, locale = 'de-DE', currency = 'EUR' } = {},
}) => {
  // eslint-disable-next-line no-implicit-coercion
  if (
    (typeof price !== 'number' && typeof price !== 'string') ||
    Number.isNaN(Number.parseFloat(price)) ||
    (!showZeroPrice && Number.parseFloat(price) === 0)
  ) {
    return noValueString
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: decimalPlaces,
  }).format(price)
}

/**
 * translate days of the week from German to English
 */
export const daysOfWeek = {
  Montag: 'Monday',
  Dienstag: 'Tuesday',
  Mittwoch: 'Wednesday',
  Donnerstag: 'Thursday',
  Freitag: 'Friday',
  Samstag: 'Saturday',
  Sonntag: 'Sunday',
  Feiertags: 'PublicHolidays',
}
export const translateDaysOfWeek = (day) => daysOfWeek[day] ?? day

/**
 * helper for get total count of bookmarks
 * @param getState
 * @param excludeBookmarkTyp
 * @returns {number}
 */
export const getNumberOfBookmarks = ({
  bookmarkState,
  excludeBookmarkTyp = false,
}) => {
  const skipKeys = [excludeBookmarkTyp]
  let totalCount = 0

  Object.keys(bookmarkState).forEach((k) => {
    if (skipKeys.includes(k)) return true

    totalCount += bookmarkState[k].count
  })

  return totalCount
}

/**
 * helper for detecting a scroll stop
 * READ MORE => https://dirask.com/posts/React-scroll-stop-event-DkBe01
 * @param element
 * @param callback
 * @param timeout
 * @returns {(function(): void)|*}
 */
export const createScrollStopListener = (element, callback, timeout) => {
  let removed = false
  let handle = null
  const onScroll = () => {
    if (handle) {
      clearTimeout(handle)
    }
    handle = setTimeout(callback, timeout || 200) // default 200 ms
  }
  element.addEventListener('scroll', onScroll)
  return () => {
    if (removed) {
      return
    }
    removed = true
    if (handle) {
      clearTimeout(handle)
    }
    element.removeEventListener('scroll', onScroll)
  }
}

/**
 * check valid geo coordinates
 * @param lat
 * @param lng
 * @returns {boolean}
 */
export const validCoordinates = (lat, lng) =>
  !!(LATITUDE_PATTERN.test(lat) && LONGITUDE_PATTERN.test(lng))

/**
 * get parse geo coordinates from url
 * @returns {{lng: number, lat: number}}
 */
export const getGeoCoordinatesFromUrl = () => {
  const params = qs.parse(window.location.search)
  let lat = parseFloat(params.lat)
  let lng = parseFloat(params.lng)

  // no valid coordinates, set as false to store
  if (!validCoordinates(lat, lng)) {
    lat = false
    lng = false
  }

  return { lat, lng }
}

/**
 * get api endpoint by entrypoint
 * @param entryPoint
 * @returns {string}
 */
export const getApiEndpointByEntrypoint = (entryPoint) => {
  let endpoint = ''

  switch (entryPoint) {
    case entryPointId.HOSPITALS:
      endpoint = apiEndpoints.detailsHospital
      break

    case entryPointId.DOCTORS:
      endpoint = apiEndpoints.detailsDoctor
      break

    case entryPointId.CAREHOMES:
      endpoint = apiEndpoints.detailsCarehomes
      break

    case entryPointId.CARESERVICES:
      endpoint = apiEndpoints.detailsCareServices
      break

    case entryPointId.DAYANDNIGHTCARE:
      endpoint = apiEndpoints.dayAndNightCare
      break

    case entryPointId.CARESUPPORT:
      endpoint = apiEndpoints.caresupport
      break

    // default:
    //     return
  }

  return endpoint
}

/**
 * get radius options by entry point
 * @param entryPoint
 * @returns {Array<{value: number|string, option: string, dataCy: string}>}
 */
export const getRadiusOptions = (entryPoint) => {
  // TODO: simplify this function maybe in a const

  // default radii
  const radiusOptions = [
    {
      value: 'ZIP',
      option: labels['searchBar.option.ZIP'],
      dataCy: 'radius-plz',
    },
    {
      value: 10000,
      option: labels['searchBar.option.10'],
      dataCy: 'radius-10',
    },
    {
      value: 20000,
      option: labels['searchBar.option.20'],
      dataCy: 'radius-20',
    },
    {
      value: 50000,
      option: labels['searchBar.option.50'],
      dataCy: 'radius-50',
    },
    {
      value: 100000,
      option: labels['searchBar.option.100'],
      dataCy: 'radius-100',
    },
    {
      value: 0,
      option: labels['searchBar.option.federalWide'],
      dataCy: 'radius-federal',
    },
  ]

  // midwives && doctors will have one additional radius value 5000
  if (
    entryPoint === entryPointId.DOCTORS ||
    entryPoint === entryPointId.MIDWIVES
  ) {
    // reorder the zip option as first element
    const [zipOption, ...radiusOptionsWithoutZIPOption] = radiusOptions
    return [
      zipOption,
      {
        value: 5000,
        option: labels['searchBar.option.5'],
        dataCy: 'radius-5',
      },
      ...radiusOptionsWithoutZIPOption,
    ]
  }

  // carehomes will have additional radius values of
  // 5000, 15000, 25000, 30000, 40000 and not 100000
  if (
    entryPoint === entryPointId.CAREHOMES ||
    entryPoint === entryPointId.DAYANDNIGHTCARE ||
    entryPoint === entryPointId.CARESERVICES ||
    entryPoint === entryPointId.CARESUPPORT
  ) {
    const radiusOptionsCare = [
      {
        value: 'ZIP',
        option: labels['searchBar.option.ZIP'],
        dataCy: 'radius-plz',
      },
      {
        value: 5000,
        option: labels['searchBar.option.5'],
        dataCy: 'radius-5',
      },
      radiusOptions[1],
      {
        value: 15000,
        option: labels['searchBar.option.15'],
        dataCy: 'radius-15',
      },
      radiusOptions[2],
      {
        value: 25000,
        option: labels['searchBar.option.25'],
        dataCy: 'radius-25',
      },
      {
        value: 30000,
        option: labels['searchBar.option.30'],
        dataCy: 'radius-30',
      },
      {
        value: 40000,
        option: labels['searchBar.option.40'],
        dataCy: 'radius-40',
      },
      radiusOptions[3],
    ]

    if (entryPoint === entryPointId.DAYANDNIGHTCARE) {
      // add 100 & federal wide
      return [...radiusOptionsCare, radiusOptions[4], radiusOptions[5]]
    }

    if (entryPoint === entryPointId.CARESERVICES) {
      return [...radiusOptionsCare, radiusOptions[5]]
    }

    // add federal wide
    return [...radiusOptionsCare, radiusOptions[5]]
  }

  return radiusOptions
}

/**
 * get option label for additional select options
 * @param resultType
 * @param selectTyp
 * @param selectValue
 */
export const getAdditionalSearchbarSelectOptionLabel = ({
  resultType,
  selectTyp,
  selectValue,
}) =>
  Array.from(ADDITIONAL_SEARCHBAR_SELECTS[resultType])
    .find((select) => select.typ === selectTyp)
    .options.find((option) => option.value === selectValue).label

/**
 * count active/selected filters
 * @param filters (Filter Collection)
 * @returns number activeFilters
 */
export const countActiveFilters = (filters) =>
  filters.reduce((counterFilters, filter) => {
    if (filter.selected) {
      return counterFilters + 1
    }
    if (filter.type === 'modal' && filter.tabs.length > 0) {
      return (
        counterFilters +
        filter.tabs.reduce(
          (tabCounter, tab) =>
            tabCounter +
            tab.options.reduce(
              (tabOptionCounter, tabOption) =>
                tabOptionCounter +
                tabOption.options.reduce(
                  (tabSubOptionCounter, tabSubOption) => {
                    if (tabSubOption.selected) {
                      return tabSubOptionCounter + 1
                    }
                    return tabSubOptionCounter
                  },
                  0
                ),
              0
            ),
          0
        )
      )
    }
    if (filter.options && filter.options.length > 0) {
      return (
        counterFilters +
        filter.options.reduce((counterOptions, option) => {
          if (option.selected) {
            return counterOptions + 1
          }
          return counterOptions
        }, 0)
      )
    }
    return counterFilters
  }, 0)

/**
 * get view headline for bookmarks and compare
 * @param selectedType
 * @param view
 * @returns {string}
 */
export const getViewHeadline = ({ selectedType, view }) => {
  let headline = labels['general.navigatorTitle']

  switch (selectedType) {
    case entryPointId.CAREHOMES:
    case entryPointId.DAYANDNIGHTCARE:
    case entryPointId.CARESUPPORT:
    case entryPointId.CARESERVICES:
      headline = labels['general.careNavigatorTitle']
      headline = `${ headline }: ${ labels[`${ view }.title`] }`
      break

    case entryPointId.DOCTORS:
      headline = `${ headline }: ${ labels[`${ view }.title`] }`
      break

    case entryPointId.HOSPITALS:
    default:
      headline = `${ headline }: ${ labels[`${ view }.title`] }`
  }

  return headline
}

/**
 * return list of keys that are different or have different values compared between two objects
 * @param obj1
 * @param obj2
 * @return []
 */
export const getObjectDiff = (obj1, obj2) =>
  Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key)
    } else if (_.isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)
      result.splice(resultKeyIndex, 1)
    }
    return result
  }, Object.keys(obj2))

/**
 * filter duplicate results
 * @param results
 * @return {[]}
 */
export const filterDuplicateResults = (results) =>
  results.reduce((acc, current) => {
    const x = acc.find((item) =>
      _.isEqual(getObjectDiff(item, current), ['id'])
    )
    if (!x) {
      return acc.concat([current])
    }
    return acc
  }, [])

/**
 * get lowest personal contribution for carehomes
 * @param contribution
 */
export const findLowestContrib = (contribution) =>
  contribution.reduce((prev, curr) =>
    parseFloat(prev.amount.replace(' €', '').replace(',', '.')) <
    parseFloat(curr.amount.replace(' €', '').replace(',', '.'))
      ? prev
      : curr
  )

/**
 * check if input string was redacted
 * @param input
 */
export const isRedactedString = (input) =>
  /.*([xX]{3,}|[9]{5,}|[0]{5,}).*/.test(input)
